import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../services/ndf-api';
import { apercuActif } from '../services/apercu';
import { peutModule, peutNdf } from '../types/ndf';

/**
 * Ce que le module Notes de frais sait de la personne connectée.
 *
 * Sert à la NAVIGATION : quels onglets afficher, où atterrir en arrivant.
 * Jamais à autoriser quoi que ce soit — chaque route revérifie côté serveur.
 *
 * L'échec est silencieux et attendu : une personne sans compte dans le module
 * reçoit un 403, et l'application doit simplement ne rien lui proposer.
 */
export function useNdfMoi() {
  const { data, isLoading } = useQuery({
    // L'IDENTITÉ FAIT PARTIE DE LA CLÉ : sans elle, la carte d'identité de
    // l'observateur pouvait être servie à la personne endossée le temps d'un
    // rendu, et c'est elle qui décide des modules affichés.
    queryKey: ['ndf', 'moi', apercuActif()],
    queryFn: () => ndfApi.moi(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  return {
    moi: data,
    chargement: isLoading,
    peutDeclarer: peutNdf(data, 'declarer'),
    peutGererFiches: peutNdf(data, 'gerer_fiches'),
    /** Approbateur, à l'un ou l'autre niveau : c'est ce qui ouvre « À valider ». */
    peutValider: peutNdf(data, 'approuver_manager') || peutNdf(data, 'approuver_rh'),
    peutApprouverRh: peutNdf(data, 'approuver_rh'),
    peutAdministrer: peutNdf(data, 'administrer'),
    /** A-t-il un accès quelconque au module ? */
    aAcces: Boolean(data),
    /** L'environnement se prête-t-il au bac à sable (pré-production) ? */
    bacASable: Boolean(data?.bac_a_sable),

    // ─── Congés ───────────────────────────────────────────────────────────
    // La carte d'identité porte les permissions de TOUS les modules : un seul
    // appel suffit, et la navigation ne peut pas décrire deux modules avec
    // deux états de chargement différents.
    congesDeclarer: peutModule(data, 'conges', 'declarer'),
    /**
     * La personne est-elle CONCERNÉE par les congés ?
     *
     * Distinct du droit de déclarer : un mandataire social peut très bien être
     * valideur ou administrateur du module (ses onglets de profil s'affichent),
     * sans être lui-même concerné par les congés, les arrêts ou les tickets.
     * Ses écrans personnels n'ont alors rien à montrer.
     */
    congesConcerne: data?.salarie?.beneficie_conges !== false && Boolean(data?.salarie),
    congesValider:
      peutModule(data, 'conges', 'approuver_manager') ||
      peutModule(data, 'conges', 'approuver_rh'),
    // R-CPT-14 est une vue RH de TOUT LE MONDE : `congesValider` ne convient
    // pas, il vaut aussi pour un manager qui ne voit que son équipe.
    congesApprouverRh: peutModule(data, 'conges', 'approuver_rh'),
    congesAbsencesRh: peutModule(data, 'conges', 'gerer_absences_rh'),
    congesTicketsPaie: peutModule(data, 'conges', 'gerer_tickets_paie'),
    congesAdministrer: peutModule(data, 'conges', 'administrer'),
  };
}
